import { createSelector } from 'reselect';

import { createParameterSelector, createValueStableProjection } from '@pressreader/appstore';
import { selectGetConfigValue } from '@pressreader/config';
import { ArticleIdentity, IArticle, IArticleIssue } from '@pressreader/content-types';
import { isNonNullable } from '@pressreader/types';

import { getState } from '../reducers';
import { ArticleState } from '../reducers/articles';

const selectState = createSelector(getState, state => state.articles);
const selectArticles = createSelector(selectState, state => state.articles);

const selectArticleIdParam = createParameterSelector<'articleId', string>('articleId');

export const selectArticleMetaById = createSelector(
    selectArticleIdParam,
    selectArticles,
    (articleId, articles): ArticleState | null => articles[articleId] || null,
);

export const selectArticleMetaByIdSelector = createSelector(
    selectArticles,
    articles =>
        (articleId: string): ArticleState | null =>
            articles[articleId] || null,
);

const selectArticleStateById = createSelector(
    selectArticleIdParam,
    selectArticles,
    (articleId, articles): ArticleState | undefined => articles[articleId],
);

export const selectArticleSelectedLanguageById = createSelector(
    selectArticleStateById,
    (articleState): IArticle['language'] | null => articleState?.currentLanguage ?? null,
);

export const selectArticleById = createSelector(selectArticleStateById, (articleState): IArticle | null => articleState?.article || null);

const getArticleByLang = ({ articleState, language }: { articleState: ArticleState; language: IArticle['language'] | null }): IArticle | null => {
    const articleInOriginalLang = articleState.article;
    if (!language) {
        return articleInOriginalLang;
    }
    return articleState.articleByLanguage?.[language] ?? (articleInOriginalLang?.language === language ? articleInOriginalLang : null);
};

/**
 * The stored language versions of the article, the original-language one first. Stays private — only
 * the invariant projections below and the last-resort `selectAnyStoredArticleVersionById` may read
 * across versions, so no other caller can reach a language-dependent field of a version it did not
 * ask for.
 */
const getStoredArticleVersions = (articleState: ArticleState | undefined): IArticle[] =>
    articleState ? [articleState.article, ...Object.values(articleState.articleByLanguage ?? {})].filter(isNonNullable) : [];

/**
 * A language-invariant field read across the stored versions: the first version holding a value
 * supplies it, the original-language one first. A version stored from a response that asked for a
 * restricted field set holds nothing in the fields it did not ask for, and another version fills
 * them in; when no version holds a value, the first version's own (empty) value stands.
 */
function readInvariantField<TField extends keyof ArticleIdentity>(versions: IArticle[], field: TField): IArticle[TField] {
    return versions.find(version => isNonNullable(version[field]))?.[field] ?? versions[0][field];
}

/**
 * The projections the language-invariant selectors below are built from. Each takes an article's
 * stored state, keyed by the article id, so the parameterized selector and the factory share one
 * projection, and each is value-stable per article: the same reference comes back for as long as the
 * projected value is unchanged, however often the version it is read from is re-mapped or replaced
 * by another one, and however many other articles are read in between.
 */
const projectStoredArticleIssue = createValueStableProjection<string, ArticleState | undefined, IArticleIssue | null>(articleState => {
    const versions = getStoredArticleVersions(articleState);

    return versions.length ? readInvariantField(versions, 'issue') : null;
});

const projectStoredArticleIdentity = createValueStableProjection<string, ArticleState | undefined, ArticleIdentity | null>(articleState => {
    const versions = getStoredArticleVersions(articleState);
    if (!versions.length) {
        return null;
    }

    return {
        id: readInvariantField(versions, 'id'),
        primaryArticleId: readInvariantField(versions, 'primaryArticleId'),
        issue: readInvariantField(versions, 'issue'),
        publication: readInvariantField(versions, 'publication'),
        date: readInvariantField(versions, 'date'),
        originalLanguage: readInvariantField(versions, 'originalLanguage'),
        articleSource: readInvariantField(versions, 'articleSource'),
    };
});

/**
 * Whether the article is in the store in any language. For existence gates, whose answer is the same
 * whichever language version answers them.
 */
export const selectIsArticleStoredById = createSelector(
    selectArticleStateById,
    (articleState): boolean => getStoredArticleVersions(articleState).length > 0,
);

/**
 * Whichever language version of the article is stored — the original-language one when it is,
 * otherwise a translated one — or `null` when none is. The last resort of a displayed-version read,
 * for an article stored in none of the languages the navigation names; every other read asks for a
 * language (`selectArticleById`, `selectArticleByIdAndLanguage`).
 */
export const selectAnyStoredArticleVersionById = createSelector(
    selectArticleStateById,
    (articleState): IArticle | null => getStoredArticleVersions(articleState)[0] ?? null,
);

/**
 * The issue the article belongs to — issue key, date, page, slugs, newspaper — or `null` when the
 * article is not stored or belongs to no issue. Every stored language version names the same issue,
 * so whichever version holds it supplies it.
 */
export const selectArticleIssueById = createSelector(selectArticleIdParam, selectArticleStateById, projectStoredArticleIssue);

/**
 * The article's identity: the fields that name the article rather than one of its language versions
 * (see `ArticleIdentity`), or `null` when the article is not stored. Every stored language version
 * carries the same values, so each field is read from whichever version holds it.
 *
 * Content and access belong to a single language version and are read elsewhere: `selectArticleById`
 * for the original-language text, `selectArticleByIdAndLanguage` for a named language, and
 * `selectDisplayedArticleById` (article-navigation) for the version on screen.
 */
export const selectArticleIdentityById = createSelector(selectArticleIdParam, selectArticleStateById, projectStoredArticleIdentity);

/** Factory form of `selectArticleIdentityById`, for lookups by several article ids in one selector. */
export const selectArticleIdentityByIdSelector = createSelector(
    selectArticles,
    articles => (articleId: string) => projectStoredArticleIdentity(articleId, articles[articleId]),
);

const selectArticleLanguageParam = createParameterSelector<'language', IArticle['language'] | null>('language');
export const selectArticleByIdAndLanguage = createSelector(
    selectArticleLanguageParam,
    selectArticleStateById,
    (language, articleState): IArticle | null =>
        articleState
            ? getArticleByLang({
                  articleState,
                  language,
              })
            : null,
);

export const selectArticleByIdSelector = createSelector(selectArticles, articles => (articleId: string) => articles[articleId]?.article || null);

export const selectArticleByIdAndLanguageSelector = createSelector(
    selectArticles,
    articles => (articleId: string, language: IArticle['language'] | null) => {
        const articleState = articles[articleId];
        if (!articleState) {
            return null;
        }
        return getArticleByLang({
            articleState,
            language,
        });
    },
);

export const selectArticleStateByIdSelector = createSelector(selectArticles, articles => (articleId: string) => articles[articleId]);

export const selectorContentIsHyphenated = createSelector(selectGetConfigValue, getConfigValue =>
    getConfigValue<boolean>('UI.Content.Hyphenated', true),
);
